'use client';

import localisePath from '@app/lib/helpers/localise-path';
import { SliceZoneContext } from '@app/lib/prismic/types/slice-zone-context';
import { safeLocalStorage } from '@app/lib/storage/safe-storage';
import { Language } from '@app/lib/store-context/Context';
import { SliceComponentProps } from '@prismicio/react';
import { Button, CTAVariant, Icon } from 'mmds';
import { useCallback, useEffect, useMemo, useState } from 'react';
import s from './PromoOverlay.module.css';
import { PromoOverlaySlice } from './types';

const STORAGE_PREFIX = 'maap-promo-overlay';
const DISMISSED_VALUE = 'dismissed';

/**
 * Ribbons (announcement / country-notice / loop-returns) all expose a
 * `ribbon-*` test id. The overlay measures their rendered height so it can be
 * offset below them; the fixed header height is handled in CSS.
 */
const RIBBON_SELECTOR = '[data-testid^="ribbon-"]';

/**
 * Props for `PromoOverlay`.
 */
export type PromoOverlayProps = SliceComponentProps<
    PromoOverlaySlice,
    SliceZoneContext
>;

type ResolvedCta = {
    label: string;
    hrefLink: string;
    openInNewTab: boolean;
};

/** CTAs always render with the secondary variant and no icon. */
const CTA_VARIANT: CTAVariant = 'secondary';

/**
 * `PromoOverlay` slice — a fixed-position promotional takeover that blends with
 * the page behind it via `mix-blend-mode` and is dismissed permanently per user
 * via `localStorage`.
 */
const PromoOverlay = ({
    slice,
    context,
}: PromoOverlayProps): JSX.Element | null => {
    const lang = (context?.lang as Language) ?? 'en-AU';
    const { title, sub_title, close_label, campaign_key } = slice.primary;
    const storageKey = useMemo(
        () => `${STORAGE_PREFIX}:${campaign_key || title || 'default'}`,
        [campaign_key, title]
    );

    // Start hidden so the server-rendered markup matches the first client paint;
    // visibility is resolved from localStorage after mount.
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // `safeLocalStorage` gracefully degrades to in-memory storage when the
        // user has denied cookie consent or storage is otherwise unavailable,
        // returning null so the overlay defaults to visible.
        const dismissed = safeLocalStorage.getItem(storageKey);
        setVisible(dismissed !== DISMISSED_VALUE);
    }, [storageKey]);

    const handleClose = useCallback(() => {
        setVisible(false);
        safeLocalStorage.setItem(storageKey, DISMISSED_VALUE);
    }, [storageKey]);

    // Measure the announcement/notice ribbon height so the overlay can sit below
    // it. The header height is a fixed design constant handled in CSS; only the
    // ribbon is conditional, so it is the one piece that must be measured. Height
    // is scroll-independent, so we read `height` rather than the rect position.
    const [ribbonOffset, setRibbonOffset] = useState(0);

    useEffect(() => {
        if (typeof window === 'undefined' || !visible) {
            return;
        }

        const measure = () => {
            const ribbons = Array.from(
                document.querySelectorAll(RIBBON_SELECTOR)
            );
            const height = ribbons.reduce((max, el) => {
                const { height: h } = el.getBoundingClientRect();
                return h > 0 ? Math.max(max, h) : max;
            }, 0);
            setRibbonOffset(height);
        };

        measure();

        let resizeObserver: ResizeObserver | null = null;
        if ('ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(() => measure());
            document
                .querySelectorAll(RIBBON_SELECTOR)
                .forEach((el) => resizeObserver?.observe(el));
        }

        window.addEventListener('resize', measure);
        // Ribbons can render after first paint (Suspense / geolocation), so
        // re-measure once shortly after mount to catch a late appearance.
        const timeout = window.setTimeout(measure, 500);

        return () => {
            window.removeEventListener('resize', measure);
            resizeObserver?.disconnect();
            window.clearTimeout(timeout);
        };
    }, [visible]);

    const ctas: ResolvedCta[] = useMemo(
        () =>
            (slice.items ?? [])
                .filter((item) => Boolean(item.button_label))
                .map((item) => ({
                    label: item.button_label as string,
                    hrefLink: localisePath(item.button_link ?? '', lang),
                    openInNewTab: Boolean(item.open_in_new_tab),
                })),
        [slice.items, lang]
    );

    if (!visible) {
        return null;
    }

    const closeLabel = close_label || 'Close';

    const styleVars = {
        '--promo-ribbon-offset': `${ribbonOffset}px`,
    } as React.CSSProperties;

    return (
        <aside
            className={s.wrapper}
            data-slice-type={slice.slice_type}
            data-slice-variation={slice.variation}
            aria-label={title || 'Promotion'}
            style={styleVars}
        >
            <div className={s.base} aria-hidden="true" />
            <div className={s.colorLayer} aria-hidden="true" />
            <div className={s.content}>
                <div className={s.closeRow}>
                    <button
                        type="button"
                        className={s.close}
                        onClick={handleClose}
                        aria-label={closeLabel}
                    >
                        <span className="mmds-component-one">{closeLabel}</span>
                        <Icon icon="close" className={s.closeIcon} />
                    </button>
                </div>
                <div className={s.center}>
                    {(title || sub_title) && (
                        <div className={s.titleCopy}>
                            {title && (
                                <h2
                                    className={`${s.title} mmds-title-two-fluid`}
                                >
                                    {title}
                                </h2>
                            )}
                            {sub_title && (
                                <p
                                    className={`${s.subtitle} mmds-copy-one`}
                                >
                                    {sub_title}
                                </p>
                            )}
                        </div>
                    )}
                    {ctas.length > 0 && (
                        <div className={s.ctas}>
                            {ctas.map((cta, index) => (
                                <Button
                                    key={`${cta.label}-${index}`}
                                    variant={CTA_VARIANT}
                                    label={cta.label}
                                    hrefLink={cta.hrefLink}
                                    lang={lang}
                                    radiusSize="large"
                                    target={
                                        cta.openInNewTab ? '_blank' : undefined
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
};

export default PromoOverlay;
